import { TopMoversDataType } from '../../types/rank.type';
import './TopMoversTable.css';
import { formatNumber } from '../../utils/utils';
import CommonTooltip from '../Common/CommonTooltip/CommonTooltip';
import { useRef, useState } from 'react';
import { CommonTooltipType } from '../../types/common.type';
import classNames from 'classnames';
import SvgImage from '../Common/SvgImage/SvgImage';

import { apiBase } from '../../api/apiBase';
export type TopMoversTableProps = {
  token?: string;
  data: TopMoversDataType[];
  isSmallScreen?: boolean;
  isLargeScreen?: boolean;
  isLoading?: boolean;
  rankType?: string;
  onStockClick?: (symbol: string) => void;
};

const TopMoversTable = ({
  isSmallScreen = false,
  isLargeScreen = false,
  data,
  isLoading,
  token,
  rankType,
  onStockClick,
}: TopMoversTableProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<CommonTooltipType>({
    content: '',
    show: false,
    x: 0,
    y: 0,
  });

  const getTopMoversChart = (ric: string) => {
    return `${apiBase()}/chart/?int=2&sym=${ric}&style=1&per=1&token=${encodeURIComponent(token || '')}`;
  };

  const constrainTooltipPosition = (e: React.MouseEvent) => {
    // Estimate tooltip dimensions (these are approximate values from CSS)
    // Calculate initial position based on item position and mouse offset
    let x = e.pageX + 10;
    let y = e.pageY;

    const elementMaxWidth = tooltipRef.current?.clientWidth || 0;
    const elementMaxHeight = tooltipRef.current?.clientHeight || 0;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (x + elementMaxWidth > viewportWidth) {
      x = x - elementMaxWidth;
    }
    if (y + elementMaxHeight > viewportHeight) {
      y = y - elementMaxHeight;
    }

    return { x, y };
  };

  return (
    <table className="top-movers-tbl">
      <thead>
        <tr className="top-movers-tbl-hdr-row">
          <th className="top-movers-tbl-hdr-idx">Rank</th>

          {!isSmallScreen && (
            <th className="top-movers-tbl-hdr-ticker">Code</th>
          )}

          {!isSmallScreen && <th className="top-movers-tbl-hdr-name">Name</th>}

          {isSmallScreen && (
            <th className="top-movers-tbl-hdr-code-name">Code / Name</th>
          )}

          <th className="top-movers-tbl-hdr-today">1 Day</th>

          {!isSmallScreen && (
            <th className="top-movers-tbl-hdr-price">Last price</th>
          )}

          {((isSmallScreen && (rankType === 'NG' || rankType === 'NL')) ||
            !isSmallScreen) && <th className="top-movers-tbl-hdr-nct">Chg</th>}

          {((isSmallScreen &&
            (rankType === 'PG' ||
              rankType === 'PL' ||
              rankType?.trim() === '' ||
              !rankType)) ||
            !isSmallScreen) && <th className="top-movers-tbl-hdr-pct">%Chg</th>}

          {((isSmallScreen && rankType === 'AV') ||
            (!isSmallScreen && !isLargeScreen && rankType !== 'AM') ||
            (isLargeScreen && !isSmallScreen)) && (
            <th className="top-movers-tbl-hdr-volume">Volume</th>
          )}

          {((isSmallScreen && rankType === 'AM') ||
            (!isSmallScreen && !isLargeScreen && rankType === 'AM') ||
            (isLargeScreen && !isSmallScreen)) && (
            <th className="top-movers-tbl-hdr-turnover">Turnover</th>
          )}
        </tr>
      </thead>

      <div className="top-movers-table-spacer"></div>

      <tbody>
        {!isLoading &&
          data &&
          data.map((item, index) => (
            <tr key={index} className="top-movers-tbl-cell-row">
              <td className="top-movers-tbl-cell top-movers-tbl-cell-idx">
                {index + 1}
              </td>
              {!isSmallScreen && (
                <td className="top-movers-tbl-cell top-movers-tbl-cell-ticker">
                  <span
                    className={classNames('top-movers-tbl-ticker', {
                      'top-movers-tbl-ticker-link': onStockClick,
                    })}
                    onClick={
                      onStockClick
                        ? () => onStockClick(item.bell_code)
                        : undefined
                    }
                  >
                    {item.symbol}
                  </span>
                </td>
              )}
              {!isSmallScreen && (
                <td className="top-movers-tbl-cell top-movers-tbl-cell-name">
                  <div
                    className={classNames(
                      'top-movers-tbl-cell-name-container',
                      {
                        'top-movers-tbl-name': onStockClick && !isSmallScreen,
                      },
                    )}
                    onMouseEnter={(e) => {
                      const constrainedPosition = constrainTooltipPosition(e);
                      setTooltip({
                        show: true,
                        x: constrainedPosition.x,
                        y: constrainedPosition.y,
                        content: item.name,
                      });
                    }}
                    onMouseMove={(e) => {
                      //if no move, do not update tooltip
                      if (e.movementX === 0 && e.movementY === 0) {
                        return;
                      }
                      const constrainedPosition = constrainTooltipPosition(e);
                      setTooltip((prev) => ({
                        ...prev,
                        x: constrainedPosition.x,
                        y: constrainedPosition.y,
                      }));
                    }}
                    onMouseLeave={() => {
                      setTooltip((prev) => ({ ...prev, show: false }));
                    }}
                    onMouseOut={() => {
                      setTooltip((prev) => ({ ...prev, show: false }));
                    }}
                  >
                    {item.name}
                  </div>
                </td>
              )}
              {isSmallScreen && (
                <td className="top-movers-tbl-cell top-movers-tbl-cell-code-name">
                  <div className="top-movers-tbl-code-name-container">
                    <div className="top-movers-tbl-code-row">
                      <span
                        className={classNames('top-movers-tbl-ticker', {
                          'top-movers-tbl-ticker-link': onStockClick,
                        })}
                        onClick={
                          onStockClick
                            ? () => onStockClick(item.bell_code)
                            : undefined
                        }
                      >
                        {item.symbol}
                      </span>
                    </div>
                    <div className="top-movers-tbl-company-name-row">
                      <div
                        className="top-movers-tbl-company-name"
                        onMouseEnter={(e) => {
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip({
                            show: true,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                            content: item.name,
                          });
                        }}
                        onMouseMove={(e) => {
                          if (e.movementX === 0 && e.movementY === 0) {
                            return;
                          }
                          const constrainedPosition =
                            constrainTooltipPosition(e);
                          setTooltip((prev) => ({
                            ...prev,
                            x: constrainedPosition.x,
                            y: constrainedPosition.y,
                          }));
                        }}
                        onMouseLeave={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                        onMouseOut={() => {
                          setTooltip((prev) => ({ ...prev, show: false }));
                        }}
                      >
                        {item.name}
                      </div>
                    </div>
                  </div>
                </td>
              )}
              {
                <td className="top-movers-tbl-cell top-movers-tbl-cell-today">
                  <div className="top-movers-tbl-cell-today-container">
                    <SvgImage svgApiUrl={getTopMoversChart(item.ric)} />
                  </div>
                </td>
              }
              {!isSmallScreen && (
                <td className="top-movers-tbl-cell top-movers-tbl-cell-price">
                  {formatNumber(item.last.toString(), item.dp, item.dp)}
                </td>
              )}
              {((isSmallScreen && (rankType === 'NG' || rankType === 'NL')) ||
                !isSmallScreen) && (
                <td
                  className={classNames(
                    'top-movers-tbl-cell top-movers-tbl-cell-nct',
                    {
                      positive: Number(item.netchng) > 0,
                      negative: Number(item.netchng) < 0,
                    },
                  )}
                >
                  {formatNumber(
                    item.netchng.toString(),
                    item.dp,
                    item.dp,
                    false,
                    true,
                    false,
                  )}
                </td>
              )}
              {((isSmallScreen &&
                (rankType === 'PG' ||
                  rankType === 'PL' ||
                  rankType?.trim() === '' ||
                  !rankType)) ||
                !isSmallScreen) && (
                <td
                  className={classNames(
                    'top-movers-tbl-cell top-movers-tbl-cell-pct',
                    {
                      positive: Number(item.pctchng) > 0,
                      negative: Number(item.pctchng) < 0,
                    },
                  )}
                >
                  {formatNumber(
                    item.pctchng.toString(),
                    2,
                    2,
                    false,
                    true,
                    false,
                    '%',
                  )}
                </td>
              )}
              {((isSmallScreen && rankType === 'AV') ||
                (!isSmallScreen && !isLargeScreen && rankType !== 'AM') ||
                (isLargeScreen && !isSmallScreen)) && (
                <td className="top-movers-tbl-cell top-movers-tbl-cell-volume">
                  {item.vol}
                </td>
              )}
              {((isSmallScreen && rankType === 'AM') ||
                (!isSmallScreen && !isLargeScreen && rankType === 'AM') ||
                (isLargeScreen && !isSmallScreen)) && (
                <td className="top-movers-tbl-cell top-movers-tbl-cell-turnover">
                  {formatNumber(item.turnover.toString(), 0, 0)}
                </td>
              )}
            </tr>
          ))}
      </tbody>
      <CommonTooltip
        ref={tooltipRef}
        content={tooltip.content}
        show={tooltip.show}
        x={tooltip.x}
        y={tooltip.y}
      />
    </table>
  );
};
export default TopMoversTable;
